import React from 'react';
import { Link } from 'react-router-dom';
import SharedLayout from '@/components/SharedLayout';
import SubpageNavigation from '@/components/SubpageNavigation';
import BreadcrumbNavigation from '@/components/BreadcrumbNavigation';
import SEOHead from '@/components/SEOHead';
import { CANONICAL_URLS } from '@/utils/canonicalUrls';

const Kanban = () => {
  const schemaData = {
    "@context": "https://schema.org", 
    "@type": "Article",
    "headline": "Kanban Method: Free Visual Workflow Management Guide & Tool",
    "description": "Learn the Kanban method for visual workflow management. Free online Kanban board with complete guide to core principles, board structure, and team efficiency.",
    "author": { "@type": "Organization", "name": "Free Pomodoro Timer" },
    "publisher": { "@type": "Organization", "name": "Free Pomodoro Timer", "url": "https://freepomodorotimer.com", "logo": { "@type": "ImageObject", "url": "https://freepomodorotimer.com/favicon.ico" } },
    "datePublished": "2024-01-15",
    "dateModified": "2026-03-13",
    "image": "https://freepomodorotimer.com/og-image.jpg",
    "mainEntityOfPage": CANONICAL_URLS.kanban
  };

  return (
    <>
      <SEOHead
        title="Kanban Board Guide | Free Visual Workflow Management Tool"
        description="Learn the Kanban method with our free online Kanban board. Master visual workflow management, WIP limits & continuous improvement for personal and team productivity."
        keywords="kanban board, kanban method, free kanban board, visual workflow management, kanban board online, agile kanban, project management tool, task board, work in progress, team productivity"
        canonicalUrl={CANONICAL_URLS.kanban}
        schemaData={schemaData}
      />
      <SharedLayout>
        <article className="text-left px-6 py-12 max-w-4xl mx-auto">
          <BreadcrumbNavigation />
          <SubpageNavigation />
        
        <h1 className="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100">Kanban Board: Free Visual Workflow Management Guide</h1>
        <p className="mb-4 text-gray-700 dark:text-gray-200">
          <strong>Kanban</strong> is a visual workflow management method originally developed by Toyota for manufacturing, now widely adopted in software development, 
          marketing, and personal productivity. This lean methodology helps teams and individuals visualize work, limit work-in-progress, and continuously improve efficiency.
        </p>
        <p className="mb-6 text-gray-700 dark:text-gray-200">
          Unlike rigid project management approaches, Kanban emphasizes flow and adaptation. It's particularly effective for knowledge work where 
          requirements change frequently and priorities shift based on feedback and new information.
        </p>

        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-4 mb-8">
          <p className="text-blue-800 dark:text-blue-200">
            <strong>Productivity Synergy:</strong> Use the <Link to="/eisenhower" className="underline hover:text-blue-600">Eisenhower Matrix</Link> to 
            prioritize tasks, then organize them in Kanban boards. Execute high-priority items with focused <Link to="/pomodoro" className="underline hover:text-blue-600">Pomodoro sessions</Link>.
          </p>
        </div>

        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">Core Kanban Principles</h2>
        <ul className="list-disc pl-6 mb-8 text-gray-700 dark:text-gray-200">
          <li className="mb-2"><strong>Visualize Work:</strong> Make all work items visible on a Kanban board</li>
          <li className="mb-2"><strong>Limit Work in Progress (WIP):</strong> Set limits on how many items can be in progress simultaneously</li>
          <li className="mb-2"><strong>Manage Flow:</strong> Focus on smooth flow of work through the system</li>
          <li className="mb-2"><strong>Make Policies Explicit:</strong> Clearly define how work moves between stages</li>
          <li className="mb-2"><strong>Improve Collaboratively:</strong> Use metrics and feedback to continuously improve</li>
        </ul>

        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">How to Set Up a Kanban Board</h2>
        <ol className="list-decimal pl-6 mb-8 text-gray-700 dark:text-gray-200">
          <li className="mb-3"><strong>Start with current process:</strong> Map your existing workflow — don't reinvent everything</li>
          <li className="mb-3"><strong>Define workflow stages:</strong> Identify how work moves from idea to completion</li>
          <li className="mb-3"><strong>Create your board:</strong> Use columns for each stage (To Do, In Progress, Done)</li>
          <li className="mb-3"><strong>Set WIP limits:</strong> Limit cards in each column to prevent overload</li>
          <li className="mb-3"><strong>Pull work, don't push:</strong> Start new work only when capacity becomes available</li>
          <li className="mb-3"><strong>Measure and optimize:</strong> Track lead time and cycle time, identify bottlenecks</li>
        </ol>

        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">Kanban Board Structures</h2>
        
        <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">Basic Personal Kanban Board</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
            <h4 className="font-bold text-yellow-800 dark:text-yellow-200">To Do</h4>
            <p className="text-yellow-700 dark:text-yellow-300 text-sm">Backlog of prioritized tasks</p>
            <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">Limit: 10-15 items</div>
          </div>
          <div className="p-4 bg-blue-100 dark:bg-blue-900 rounded-lg">
            <h4 className="font-bold text-blue-800 dark:text-blue-200">In Progress</h4>
            <p className="text-blue-700 dark:text-blue-300 text-sm">Currently active tasks</p>
            <div className="text-xs text-blue-600 dark:text-blue-400 mt-2">Limit: 2-3 items</div>
          </div>
          <div className="p-4 bg-green-100 dark:bg-green-900 rounded-lg">
            <h4 className="font-bold text-green-800 dark:text-green-200">Done</h4>
            <p className="text-green-700 dark:text-green-300 text-sm">Completed tasks</p>
            <div className="text-xs text-green-600 dark:text-green-400 mt-2">Archive weekly</div>
          </div>
        </div>

        <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">Advanced Team Kanban Board</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-8">
          <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg text-center">
            <h5 className="font-semibold text-gray-800 dark:text-gray-200 text-sm">Backlog</h5>
          </div>
          <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-lg text-center">
            <h5 className="font-semibold text-orange-800 dark:text-orange-200 text-sm">Selected</h5>
          </div>
          <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg text-center">
            <h5 className="font-semibold text-blue-800 dark:text-blue-200 text-sm">Development</h5>
          </div>
          <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg text-center">
            <h5 className="font-semibold text-purple-800 dark:text-purple-200 text-sm">Review</h5>
          </div>
          <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg text-center">
            <h5 className="font-semibold text-green-800 dark:text-green-200 text-sm">Done</h5>
          </div>
        </div>

        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">Key Kanban Metrics</h2>
        <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-lg mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">Cycle Time</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Time from start to completion of a work item</p>
              <p className="text-xs text-gray-500 dark:text-gray-500">Target: Consistent and predictable</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">Lead Time</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Time from request to delivery</p>
              <p className="text-xs text-gray-500 dark:text-gray-500">Target: Minimize customer wait time</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">Throughput</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Number of items completed per time period</p>
              <p className="text-xs text-gray-500 dark:text-gray-500">Target: Steady, sustainable pace</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">Work in Progress</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Number of items actively being worked on</p>
              <p className="text-xs text-gray-500 dark:text-gray-500">Target: Stay within WIP limits</p>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">Kanban for Different Use Cases</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">Personal Productivity</h3>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li>• Daily task management</li>
              <li>• Project tracking</li>
              <li>• Habit formation</li>
              <li>• Learning objectives</li>
            </ul>
          </div>
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">Team Collaboration</h3>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li>• Software development</li>
              <li>• Marketing campaigns</li>
              <li>• Content creation</li>
              <li>• Customer support</li>
            </ul>
          </div>
        </div>

        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">Common Kanban Board Mistakes</h2>
        <ul className="list-disc pl-6 mb-8 text-gray-700 dark:text-gray-200">
          <li className="mb-3"><strong>Ignoring WIP limits:</strong> Too many items in progress leads to context switching and delays</li>
          <li className="mb-3"><strong>Creating too many columns:</strong> Overly complex boards hide problems instead of revealing them</li>
          <li className="mb-3"><strong>Not measuring flow:</strong> Without metrics, you can't identify bottlenecks or improvements</li>
          <li className="mb-3"><strong>Pushing work:</strong> Forcing work into columns creates artificial pressure and reduces quality</li>
          <li className="mb-3"><strong>Skipping retrospectives:</strong> Regular improvement reviews are essential for continuous enhancement</li>
        </ul>

        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-bold mb-3 text-green-800 dark:text-green-200">🚀 Supercharge Your Kanban Workflow</h3>
          <ul className="space-y-2 text-green-700 dark:text-green-300">
            <li>• Prioritize board items using the <Link to="/eisenhower" className="font-semibold underline">Eisenhower Matrix</Link></li>
            <li>• Work on high-priority tasks with <Link to="/pomodoro" className="font-semibold underline">Pomodoro sessions</Link> for deep focus</li>
            <li>• Review and update your board during weekly planning sessions</li>
            <li>• Use color coding for task types, priorities, or team members</li>
          </ul>
        </div>

        {/* Related Tools Section */}
        <section className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Boost Your Kanban Workflow</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Free Eisenhower Matrix</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Before adding tasks to your Kanban board, prioritize them using the Eisenhower Matrix to ensure you're working on what matters most.
              </p>
              <Link to="/eisenhower" className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                Prioritize Tasks →
              </Link>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Free Pomodoro Timer</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Use focused 25-minute work sessions to tackle tasks from your "In Progress" column with maximum concentration and efficiency.
              </p>
              <Link to="/pomodoro" className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                Start Focused Work →
              </Link>
            </div>
          </div>
        </section>
        </article>
      </SharedLayout>
    </>
  );
};

export default Kanban;